/**
 * Lane SCENE-PLAINS, BUILDINGS: red carpets (docs/art/scenery-ref/red-carpet/SOURCES.md). Flat ground decals in 2:1
 * iso, painted pixel by pixel from tile coordinates: a runner (hall aisle, welcome strip at a gate), a ceremony carpet
 * (medallion) and a stage carpet (duel-stage / lei-tai look). Original motifs only (lozenge chain, ring and eight-point
 * star, key-pattern band); ideas from the refs, nothing traced. Figures stand ON them: `flat` sprites sort at their
 * far corner (passes/kitProps.ts), so nothing standing is ever covered. No collision (decals are walkable).
 */
import { hash2 } from '@nine/shared';
import { M, snap, type Ramp, type RGB } from '../buildings';
import type { Sprite } from '../scenery';
import { K, propPix } from './parts';
import { fin } from './propBase';

export interface FlatSprite extends Sprite {
  /** Footprint in tiles (x, y): the sprite sorts at its far corner (kitProps). */
  flat: readonly [number, number];
}

const PX = 13; // pixels across one tile, perpendicular to an edge (16 × 8 diamond)

type Tex = (x: number, y: number, lx: number, ly: number) => RGB;

const gold = (l: number, x: number, y: number): RGB => snap(M.gold, l, x, y, 0.3);
const redf = (l: number, x: number, y: number): RGB => snap(M.red, l, x, y, 0.3);

/** Weave: the field's light changes by a quiet checker of threads, and a rare darker fleck. */
const weave = (x: number, y: number): number => (((Math.floor(x * 8) + Math.floor(y * 8)) & 1 ? 0.04 : -0.03) + (hash2(Math.floor(x * 9), Math.floor(y * 9)) > 0.93 ? -0.1 : 0));

function bordered(inner: Tex, wide = false): Tex {
  return (x, y, lx, ly) => {
    const e = Math.min(x, lx - x, y, ly - y) * PX;
    const W = wide ? 4 : 0;
    if (e < 1) return M.dark[1]!;
    if (e < 3) return gold(0.62 + (x + y) % 2 * 0.06, x * 16 | 0, y * 16 | 0);
    if (e < 5 + W) return snap(K.maroon, 0.35, (x * 16) | 0, (y * 16) | 0, 0.2);
    if (e < 6.4 + W) return gold(0.5, (x * 16) | 0, (y * 16) | 0);
    if (wide && e < 12) {
      // A band of key pattern between two lines.
      const t = Math.floor(x * 5 + y * 5);
      const c = Math.floor((x - y) * 5);
      return ((t + c) & 3) === 0 ? gold(0.55, t, c) : snap(K.maroon, 0.5, t, c, 0.2);
    }
    if (wide && e < 13.4) return gold(0.5, (x * 16) | 0, (y * 16) | 0);
    return inner(x, y, lx, ly);
  };
}

/** A chain of lozenges along the carpet's length with dots between. */
function lozengeField(along: 'x' | 'y', step = 2): Tex {
  return (x, y, lx, ly) => {
    const t = along === 'x' ? x : y;
    const c = along === 'x' ? y - ly / 2 : x - lx / 2;
    const dt = ((t % step) + step) % step - step / 2;
    const d = (Math.abs(dt) + Math.abs(c)) * PX;
    const base = 0.66 + weave(x, y);
    if (d < 8.5 && d > 7) return gold(0.7, (x * 16) | 0, (y * 16) | 0);
    if (d < 4) return Math.abs(dt) + Math.abs(c) < 0.09 ? M.dark[2]! : gold(0.62 + (d < 2 ? 0.1 : 0), (x * 16) | 0, (y * 16) | 0);
    if (d < 7) return snap(K.maroon, 0.62, (x * 16) | 0, (y * 16) | 0, 0.2);
    // Small dots between the lozenges.
    if (Math.abs(c) < 0.06 && Math.abs(Math.abs(dt) - step / 2) < 0.1 + 0.0) return gold(0.6, (x * 16) | 0, 0);
    return redf(base, (x * 16) | 0, (y * 16) | 0);
  };
}

/** A medallion: two rings, an eight-point star and a red hub; lozenges at the four corners of the field. */
function medallion(R: number, field: Ramp | null = null): Tex {
  return (x, y, lx, ly) => {
    const cx = lx / 2;
    const cy = ly / 2;
    const dx = (x - cx) * PX;
    const dy = (y - cy) * PX;
    const r = Math.hypot(dx, dy);
    const th = Math.atan2(dy, dx);
    const base = 0.62 + weave(x, y);
    const star = R * (0.62 + 0.38 * Math.abs(Math.cos(th * 4)));
    if (Math.abs(r - R * 1.6) < 1.2 || Math.abs(r - R * 1.32) < 0.7) return gold(0.68, (x * 16) | 0, (y * 16) | 0);
    if (r < 3) return snap(K.maroon, 0.6, (x * 16) | 0, (y * 16) | 0, 0.2);
    if (r < star) return gold(0.55 + (r < star * 0.6 ? 0.1 : 0), (x * 16) | 0, (y * 16) | 0);
    for (const [qx, qy] of [[1.7, 1.7], [lx - 1.7, 1.7], [1.7, ly - 1.7], [lx - 1.7, ly - 1.7]] as const)
      if ((Math.abs(x - qx) + Math.abs(y - qy)) * PX < 6) return gold(0.58, (x * 16) | 0, (y * 16) | 0);
    return field ? snap(field, base, (x * 16) | 0, (y * 16) | 0, 0.3) : redf(base, (x * 16) | 0, (y * 16) | 0);
  };
}

function paint(lx: number, ly: number, tex: Tex): FlatSprite {
  const pad = 2;
  const W = 16 * (lx + ly) + pad * 2;
  const H = 8 * (lx + ly) + pad * 2;
  const ox = 16 * ly + pad;
  const oy = pad;
  const p = propPix(W, H, ox + 8 * (lx - ly), oy + 4 * (lx + ly));
  for (let Y = 0; Y < H; Y++)
    for (let X = 0; X < W; X++) {
      const a = (X + 0.5 - ox) / 16;
      const b = (Y + 0.5 - oy) / 8;
      const x = (a + b) / 2;
      const y = (b - a) / 2;
      if (x < 0 || y < 0 || x >= lx || y >= ly) continue;
      p.px.set(X, Y, tex(x, y, lx, ly));
    }
  return { ...fin(p), flat: [lx, ly] };
}

/** A runner `len` tiles long and 2 wide along x or y (hall aisle, welcome strip). */
export const runner = (along: 'x' | 'y', len: number): FlatSprite =>
  paint(along === 'x' ? len : 2, along === 'x' ? 2 : len, bordered(lozengeField(along)));

/** An entrance rug at the foot of a stair or door: 3 tiles out, 2 wide (lantern-quarter ref), medallion-less. */
export const doorRug = (along: 'x' | 'y'): FlatSprite =>
  paint(along === 'x' ? 3 : 2, along === 'x' ? 2 : 3, bordered(lozengeField(along, 3)));

/** A ceremony carpet (medallion, border): 8 × 6 tiles along x, 6 × 8 along y. */
export const ceremony = (along: 'x' | 'y'): FlatSprite => paint(along === 'x' ? 8 : 6, along === 'x' ? 6 : 8, bordered(medallion(7)));

/** A stage carpet: 8 × 8, a deeper red with a wide key-pattern border and a big ring (the duel stage's look). */
export const stageCarpet = (): FlatSprite => paint(8, 8, bordered(medallion(9, M.lacquer), true));

export const MAKERS_CARPET = {
  runnerShortX: () => runner('x', 6),
  runnerShortY: () => runner('y', 6),
  runnerLongX: () => runner('x', 12),
  runnerLongY: () => runner('y', 12),
  ceremonyX: () => ceremony('x'),
  ceremonyY: () => ceremony('y'),
  doorRugX: () => doorRug('x'),
  doorRugY: () => doorRug('y'),
  stageCarpet,
} satisfies Record<string, () => Sprite>;
export type PropIdCarpet = keyof typeof MAKERS_CARPET;
